import { sharedProps } from "../services";
import { Gateway } from "@lernetz/svelte-model-api";
import type { SearchResult } from "@lernetz/svelte-search";
import type { CollectionMaterial, Progress, LastVisited } from "../types";
import { authentication } from "@microsoft/teams-js";

export interface SearchParams {
    query: string;
    sort: string;
    offset: number;
    limit: number;
    filters: Record<string, string[]>;
    progress?: boolean;
}

export type MaterialSearchParams = {
    scope?: "my" | "public";
    query: string;
    sort: string;
    pageSize: number;
    page: number;
    lang: string;
    filters: Record<string, string[]>;
    progress?: boolean;
};


export type MaterialSearchMetadata = {
    facetDistribution: Record<string, Record<string, number>>;
};

export type MaterialSearchResponse = SearchResult<CollectionMaterial, MaterialSearchMetadata>;


export class FlinkGateway extends Gateway {

    async material( publicId: string ) {
        let headers = await this.prepareHeaders();
        return this.base.jsonApi( `/s/${ publicId }/api/material`, {}, headers );
    }

    async searchCollectionMaterials( collectionPublicId: string, searchParam: SearchParams ) {
        let headers = await this.prepareHeaders();
        return this.base
            .jsonApi( `s/${ collectionPublicId }/api/search`, searchParam, headers )
            .then<MaterialSearchResponse>( this.in );
    }

    async lastVisited() {
        let headers = await this.prepareHeaders();
        return this.base
            .jsonApi( `api/lastVisited/`, { limit: 4, modelName: "App\\Models\\Material" }, headers )
            .then<LastVisited[]>( this.in );
    }

    async shareProgress( publicId: string ) {
        let headers = await this.prepareHeaders();
        return this.base
            .jsonApi( `api/shareProgress/`, { publicId }, headers )
            .then<Progress>( this.in );
    }

    async shareProgresssAverage( publicIds: string[] ) {
        let headers = await this.prepareHeaders();
        return this.base
            .jsonApi( `api/shareProgressesAverage/`, { publicIds }, headers )
            .then<{ average: number; }>( this.in );
    }

    private async prepareHeaders() {
        //let token = sharedProps.accessToken;
        let token = await this.getAuthToken();
        return { Accept: "application/json", "Content-Type": "application/json", Authorization: "Bearer " + token };
    }

    private async getAuthToken() {
        if( sharedProps.env == 'local' ) {
            // TODO get token from backend
            // teams
            return "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6IkNOdjBPSTNSd3FsSEZFVm5hb01Bc2hDSDJYRSJ9.eyJhdWQiOiJjZTBkYzY5NS1mYWFhLTQ1MWEtYTcyMi00OTM2MzQ4NmMxMDkiLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vNGE4NzkzZjktYWMxOS00NmU4LWJiNDYtY2QzMTBkZGQyNjBiL3YyLjAiLCJpYXQiOjE3NDc5MTIzMTQsIm5iZiI6MTc0NzkxMjMxNCwiZXhwIjoxNzQ3OTE2ODc0LCJhaW8iOiJBWFFBaS84WkFBQUF0ZmdiWkRZU05YZWFqNWQzV1o3dHl1dnJmYVBPNEVCV1ArYzhHMy9NTGRCTDJSYU4zdTBFM1lLT2RPbGdhMXdxVlhRUGRTNVNQaldmNTI3bkxTRFIzM0ZIMjM0UFNOajNJMXBWaHFlbzJ5aExjSDAwSUxkeUdKcTJ0a3Jub1FjNWFqbTR1RDlCbUdwbWZtMDVORXpWbGc9PSIsImF6cCI6IjVlM2NlNmMwLTJiMWYtNDI4NS04ZDRiLTc1ZWU3ODc4NzM0NiIsImF6cGFjciI6IjAiLCJncm91cHMiOlsiNjBhYjRhZmItNDNjNC00ODc2LWE1OTItNDJiODM3ZjY4N2MwIl0sIm5hbWUiOiJMZXJuZXR6IExlaHJwZXJzb24iLCJvaWQiOiI3Mjk3MjRkZS1iYjdmLTQzYTMtYmFkZC1jZDM0MDU1YzM2ZTMiLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiJsZXJuZXR6LmxlaHJwZXJzb25AcG9seWJhdS5jaCIsInJoIjoiMS5BVG9BLVpPSFNobXM2RWE3UnMweERkMG1DNVhHRGM2cS1ocEZweUpKTmpTR3dRbmhBQmc2QUEuIiwic2NwIjoiYWNjZXNzIiwic2lkIjoiMDA0ZmFlODktMDJhMC1jZTkyLTZjYjgtYWJjNDkzMDNlNzY4Iiwic3ViIjoiV05IM0RvWTlYcFVnb0JXY1ZZaUpEblZRZGZodV9HMUkwbFNhY0Y2b1hWdyIsInRpZCI6IjRhODc5M2Y5LWFjMTktNDZlOC1iYjQ2LWNkMzEwZGRkMjYwYiIsInV0aSI6InRYdHhhLWNoR2syNHhWLW1QRW9QQUEiLCJ2ZXIiOiIyLjAiLCJ4bXNfZnRkIjoiaGthS2d3ZWk2NG9YeHhDcUxkRUJXV1JKcWxYR3hhaWZNSzlSN1ZuaFM2VUJaWFZ5YjNCbGQyVnpkQzFrYzIxeiJ9.B9S5v2Ce-bjrparci12WXgOBNB4kWos-48L-PAYmHKZETFMOdNqfX_z7Jt97TKF-3OJcBghPhw9LNnDntGXce1AlDY2F2-gaiYRpChh9hV9BgizZ-64X6HJMbyl2o-iVaAjvHjXSme-ZEISJs0Wagal5zJc-PwMUd9mvQWs-kr7_AxsQuT5bOmc2ml6t2dde2H-70bMUSSBW9jcQz00XSk04ZrrzfKzTEeXVJTzbHQ93lXconZ8A55aSFlOUR1DEScOB_vLkJYMWxCWvkC5l-vCiGYv9PlbMeeTtMvG7W9v2WEACSs2gLEZCpj60K3RYX37j7UINkTMgfCKz6WXetw";
        } else {
            return await authentication.getAuthToken();
        }
    }


}



